/**
 * Project goal (docs/project-goals.md, docs/ux/project-tab-spec.md §1.5): one encrypted `goal`
 * entity per project, with ordered milestones and checkable criteria. Text, milestones and
 * evidence links are private content; the hub sees ciphertext revisions only.
 *
 * Progress is criteria VERIFIED by evidence (a landed landing, an available build, a done task)
 * and milestones reached. It is never a percentage from task counts.
 */
import { z } from 'zod';
import { stableEntityId } from './digests.js';
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const GOAL_STATUSES = ['active', 'paused', 'achieved'];
export const GoalStatus = z.enum(GOAL_STATUSES);
export const GoalEvidenceRef = z.strictObject({
    kind: z.enum(['landing', 'build', 'task']),
    id: z.string().min(1).max(128),
});
export const GoalCriterion = z.strictObject({
    id: z.string().min(1).max(64),
    text: z.string().min(1).max(300),
    evidence: z.array(GoalEvidenceRef).max(8).default([]),
});
export const GoalMilestone = z.strictObject({
    id: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    criteria: z.array(GoalCriterion).max(24).default([]),
});
/** Entity value for kind `goal`. `text` is the user's objective, kept exactly as written. */
export const GoalRecord = z.strictObject({
    projectId: z.string().min(1).max(128),
    text: z.string().trim().min(1).max(4000),
    status: GoalStatus.default('active'),
    milestones: z.array(GoalMilestone).max(16).default([]),
    /** Optimistic revision: a save must name the revision it read. */
    revision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    updatedAt: Ts,
});
export const goalEntityId = (projectId) => stableEntityId('goal', projectId);
//# sourceMappingURL=goals.js.map